import React, { useEffect, useState } from 'react';
import moment, { Moment, unitOfTime } from 'moment';
import { connect } from 'react-redux';
import FilterButton from 'commons/src/components/buttons/FilterButton';
import PrimaryButton from 'commons/src/components/buttons/PrimaryButton';
import DatePicker from 'commons/src/components/DatePicker';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { dateFormats } from 'commons/src/constants';
import { Resolution, TimePeriod } from 'commons/src/models/commonEnums';
import { Store } from '../../../reducers';

type ParentProps = {
    selectedPeriod: TimePeriod;
    setSelectedPeriod: (period: TimePeriod, fromDate: string, toDate: string, resolution: Resolution) => void;
};

type StateProps = {
    dateFormat: string;
};

type Props = ParentProps & StateProps;

const EnergyInsightTimeSelector = ({ selectedPeriod, setSelectedPeriod, dateFormat }: Props): React.ReactElement => {
    const [startDate, setStartDate] = useState<Moment | null>(null);
    const [endDate, setEndDate] = useState<Moment | null>(null);
    const [displayDatePicker, setDisplayDatePicker] = useState(false);

    const isOutsideDateRange = (day: Moment): boolean => {
        if (!moment.isMoment(day)) return false;

        const today = moment();
        const oneWeekAgo = today.subtract(7, 'days');

        return day.isAfter(oneWeekAgo.startOf('day'));
    };

    const isOutsideEndDateRange = (day: Moment, selectedStartDate: Moment | null): boolean => {
        if (!moment.isMoment(day)) return false;
        const weekAfterStartDate = selectedStartDate
            ? moment(day).endOf('day').diff(moment(selectedStartDate).add(7, 'days').endOf('day')) < 0
            : false;
        const thirtyDaysAfterStartDate = selectedStartDate
            ? moment(day).endOf('day').diff(moment(selectedStartDate).add(30, 'days').endOf('day')) > 0
            : false;
        const isBeforeStartDate = moment(day).endOf('day').isBefore(moment(selectedStartDate).endOf('day'));
        if (isBeforeStartDate || thirtyDaysAfterStartDate || weekAfterStartDate) {
            return true;
        }
        const today = moment();

        return day.isAfter(today.startOf('day'));
    };

    const onStartDateChanged = (date: Moment): void => {
        if (moment(date).startOf('day').valueOf() !== moment(startDate).valueOf()) {
            setStartDate(date);
            const endDateMoreThan30DaysAfterStart =
                endDate && date && moment(date).add(30, 'days').endOf('day').isBefore(endDate);
            const startDateAfterEndDate = endDate && date && date.endOf('day').isAfter(endDate.endOf('day'));
            if (endDateMoreThan30DaysAfterStart || startDateAfterEndDate) {
                setEndDate(null);
            }
        }
    };

    const selectPeriod = (period: TimePeriod): void => {
        const to = moment().subtract(1, 'day').format('YYYY-MM-DD');
        const from =
            period === TimePeriod.twoWeeks
                ? moment().subtract(2, 'week')
                : moment().subtract(1, period as unitOfTime.DurationConstructor);
        const resolution = Resolution.day;
        if (period !== TimePeriod.custom) {
            setStartDate(null);
            setEndDate(null);
        }
        setSelectedPeriod(period, from.format('YYYY-MM-DD'), to, resolution);
    };

    const fetchCustomTimeSpan = (): void => {
        if (startDate && endDate) {
            const timeSpan = endDate.diff(startDate, 'days');
            const resolution = timeSpan > 1 ? Resolution.day : Resolution.hour;
            setSelectedPeriod(
                TimePeriod.custom,
                startDate.format('YYYY-MM-DD'),
                endDate.format('YYYY-MM-DD'),
                resolution
            );
        }
    };

    useEffect(() => {
        fetchCustomTimeSpan();
    }, [startDate, endDate]);

    const onEndDateChanged = (date: Moment): void => {
        if (moment(date).startOf('day').valueOf() !== moment(endDate).valueOf()) {
            setEndDate(date);
        }
    };

    const selectCustomDate = (): void => {
        setDisplayDatePicker(true);
    };

    const closeSelectCustomDate = (): void => {
        setDisplayDatePicker(false);
    };

    const customTitle =
        startDate && endDate
            ? `${startDate.format(dateFormats[dateFormat as keyof typeof dateFormats].shortFormat)} - ${endDate.format(
                  dateFormats[dateFormat as keyof typeof dateFormats].shortFormat
              )}`
            : 'TimeSelector.Custom';

    return (
        <div className="building-insight__time-selector__elements">
            {displayDatePicker ? (
                <>
                    <PrimaryButton
                        color="tertiary"
                        icon={<MaterialIcon name={displayDatePicker ? 'close' : 'date_range'} />}
                        onClick={closeSelectCustomDate}
                        testAttr="date-picker"
                    />
                    <div className="small-padding-right">
                        <DatePicker
                            id="startDate"
                            onChange={onStartDateChanged}
                            dateFormat={dateFormat as keyof typeof dateFormats}
                            selectedDate={startDate}
                            initialVisibleMonth={moment()}
                            disabledDate={(day): boolean => isOutsideDateRange(day)}
                            placeholder="StartDate"
                        />
                    </div>
                    <DatePicker
                        id="endDate"
                        onChange={onEndDateChanged}
                        dateFormat={dateFormat as keyof typeof dateFormats}
                        selectedDate={endDate}
                        initialVisibleMonth={moment()}
                        disabledDate={(day): boolean => isOutsideEndDateRange(day, startDate)}
                        placeholder="EndDate"
                    />
                </>
            ) : (
                <>
                    <FilterButton
                        title="TimeSelector.Last7Days"
                        isSelected={selectedPeriod === TimePeriod.week}
                        onClick={(): void => selectPeriod(TimePeriod.week)}
                    />
                    <FilterButton
                        title="TimeSelector.Last14Days"
                        isSelected={selectedPeriod === TimePeriod.twoWeeks}
                        onClick={(): void => selectPeriod(TimePeriod.twoWeeks)}
                    />
                    <FilterButton
                        title="TimeSelector.Last30Days"
                        isSelected={selectedPeriod === TimePeriod.month}
                        onClick={(): void => selectPeriod(TimePeriod.month)}
                    />
                    <FilterButton
                        title={customTitle}
                        isSelected={selectedPeriod === TimePeriod.custom}
                        onClick={selectCustomDate}
                    />
                </>
            )}
        </div>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        userSettings: { dateFormat },
    } = state;
    return {
        dateFormat,
    };
};

export default connect(mapStateToProps)(EnergyInsightTimeSelector);
